/*!
 * tone-pulse.js — emit an ultrasonic repellent in bursts instead of one
 * unbroken tone.
 *
 * WHY, ON BOTH AXES
 * -----------------
 * EFFICACY. Pulsing with varying intervals is what commercial ultrasonic
 * repellers do, and the reason is habituation: a steady tone is something a
 * rodent stops reacting to within weeks, while varying pulse width and
 * interval prevents a predictable pattern from forming. Field parameters cited
 * for such devices are pulse widths of 0.5-2 s and inter-pulse intervals of
 * 2-10 s, randomised. Our own /bird-repeller was already built this way — it
 * was the three others that emitted continuously.
 *
 * LOAD. Measured 2026-08-22 (tests/scratch/worst-case-bench.mjs) against real
 * music normalised to full scale, as coil dissipation per minute with the
 * voice coil's rising impedance accounted for:
 *
 *     /dog-repellent        99% duty   14.8x music
 *     /rodent-repellent    100% duty   13.8x
 *     /cockroach-repellent  99% duty    9.8x
 *     /bird-repeller  (already pulsed, 17-35% duty)   0.2-0.9x
 *
 * Duty is the whole difference. Pulsing does not lower the level a pest hears
 * while a burst is playing; it lowers what the coil integrates.
 *
 * WHERE IT GOES
 * A dedicated gate node between the tool's master gain and its output — NOT
 * the master gain itself, which the tools already automate for their own fades
 * and set directly from the volume slider. Two automations on one AudioParam
 * fight; a separate node cannot.
 *
 *     var gate = ctx.createGain();
 *     masterGain.connect(gate); gate.connect(out);
 *     var pulse = TonePulse.attach(ctx, gate);
 *     pulse.start();   // when the tool starts
 *     pulse.stop();    // when it stops — leaves the gate open
 *
 * Classic script (ES5, no imports) — safe for @light() + the ES2019 guard.
 */
(function (root) {
    'use strict';

    var DEFAULTS = {
        onMinSec: 0.5,
        onMaxSec: 2.0,
        gapMinSec: 2.0,
        gapMaxSec: 10.0,
        // Ramp the edges instead of stepping: a hard gate on a 19 kHz tone
        // makes an audible click at the discontinuity, and a click is
        // broadband — the one thing an ultrasonic tool must not emit.
        edgeSec: 0.02
    };

    function rnd(a, b) { return a + Math.random() * (b - a); }

    function attach(ctx, gate, opts) {
        var cfg = {
            onMinSec: (opts && opts.onMinSec) || DEFAULTS.onMinSec,
            onMaxSec: (opts && opts.onMaxSec) || DEFAULTS.onMaxSec,
            gapMinSec: (opts && opts.gapMinSec) || DEFAULTS.gapMinSec,
            gapMaxSec: (opts && opts.gapMaxSec) || DEFAULTS.gapMaxSec,
            edgeSec: (opts && opts.edgeSec) || DEFAULTS.edgeSec
        };
        var timer = null, running = false;

        function scheduleOne() {
            if (!running || !ctx || !gate) return;
            var onS = rnd(cfg.onMinSec, cfg.onMaxSec);
            var gapS = rnd(cfg.gapMinSec, cfg.gapMaxSec);
            var t = ctx.currentTime + 0.01;
            var e = cfg.edgeSec;
            try {
                gate.gain.cancelScheduledValues(t);
                gate.gain.setValueAtTime(0.0001, t);
                gate.gain.exponentialRampToValueAtTime(1, t + e);
                gate.gain.setValueAtTime(1, t + onS - e);
                gate.gain.exponentialRampToValueAtTime(0.0001, t + onS);
            } catch (err) { return; }
            timer = setTimeout(scheduleOne, (onS + gapS) * 1000);
        }

        function start() {
            if (running) return;
            running = true;
            scheduleOne();
        }

        function stop() {
            running = false;
            if (timer) { clearTimeout(timer); timer = null; }
            // Leave the gate OPEN: the tool's own stop path fades the master
            // gain, and a gate left at zero would silence the next start.
            try {
                gate.gain.cancelScheduledValues(ctx.currentTime);
                gate.gain.setValueAtTime(1, ctx.currentTime);
            } catch (err) {}
        }

        /** Expected long-run duty, for tests and telemetry. */
        function expectedDuty() {
            var on = (cfg.onMinSec + cfg.onMaxSec) / 2;
            var gap = (cfg.gapMinSec + cfg.gapMaxSec) / 2;
            return on / (on + gap);
        }

        return { start: start, stop: stop, expectedDuty: expectedDuty, config: cfg };
    }

    root.TonePulse = { attach: attach, DEFAULTS: DEFAULTS };
})(typeof window !== 'undefined' ? window : this);
